"use client";

import { useRef, useState } from "react";
import { rashis } from "@ishvaram/domain-data/rashis";
import { getCurrentTransits, type SadeSatiInfo } from "@/lib/api/transits";

/**
 * "Which phase am I in now?" for /sade-sati/phases/: pick a Moon sign and the
 * engine's live transit (GET /transits/current for today's IST date) says
 * whether Sade Sati runs for it, and which phase.
 *
 * Bundle: the only static imports are react, @/lib/api/transits and
 * @ishvaram/domain-data/rashis, and the last two already ship in the root
 * layout's client graph (EphemerisTransitRibbon). So this island adds its own
 * code and shares no module with /sade-sati's own first-load graph — no
 * react-query, ist-date, RashiPicker or saturn-status, which a shared async
 * chunk could re-batch (tests/explainer-tool-islands.test.ts).
 *
 * v1 emits no analytics event (owner decision 2026-10-05): trackCalculatorUsed
 * also fires free_tool_completed, which the sade_sati_result reach SLI counts
 * by route prefix, and this page renders no offer that could reach those
 * visitors. No offer and no chat entry here either.
 *
 * The server HTML holds the 12 sign buttons and the phase track only: no
 * Saturn sign, no date (rules 23/24, 29). The date is computed on the tap.
 */

type Phase = "rising" | "peak" | "setting";

const PHASE_STEPS: ReadonlyArray<{ phase: Phase; label: string; where: string }> = [
  { phase: "rising", label: "Rising", where: "Saturn in the 12th sign from your Moon" },
  { phase: "peak", label: "Peak", where: "Saturn over your Moon sign" },
  { phase: "setting", label: "Setting", where: "Saturn in the 2nd sign from your Moon" },
];

type Lookup =
  | { state: "idle" }
  | { state: "loading"; rashi: string }
  | { state: "error"; rashi: string }
  | { state: "resolved"; rashi: string; sadeSati: SadeSatiInfo | null; saturnSign: string | null };

type Resolved = Extract<Lookup, { state: "resolved" }>;

/** Today's calendar date in IST (YYYY-MM-DD), the date the engine is asked about. */
function todayInIST(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function useSadeSatiPhaseLookup() {
  const [lookup, setLookup] = useState<Lookup>({ state: "idle" });
  const requestRef = useRef(0);
  const cacheRef = useRef(new Map<string, Resolved>());

  async function select(rashi: string) {
    const request = ++requestRef.current;
    // A second tap on the shown sign clears it; on an error it retries.
    if ((lookup.state === "loading" || lookup.state === "resolved") && lookup.rashi === rashi) {
      setLookup({ state: "idle" });
      return;
    }
    const date = todayInIST();
    const cached = cacheRef.current.get(`${rashi}:${date}`);
    if (cached) {
      setLookup(cached);
      return;
    }
    setLookup({ state: "loading", rashi });
    try {
      const transits = await getCurrentTransits(rashi, date);
      const saturn = transits.active_transits.find((entry) => entry.planet_id === "saturn");
      const resolved: Resolved = {
        state: "resolved",
        rashi,
        sadeSati: transits.sade_sati ?? null,
        saturnSign: saturn?.current_rashi_name_en ?? null,
      };
      cacheRef.current.set(`${rashi}:${date}`, resolved);
      if (request === requestRef.current) setLookup(resolved);
    } catch {
      if (request === requestRef.current) setLookup({ state: "error", rashi });
    }
  }

  return { lookup, select };
}

export default function SadeSatiPhaseFinder() {
  const { lookup, select } = useSadeSatiPhaseLookup();
  const selected = lookup.state === "idle" ? null : lookup.rashi;
  const activePhase =
    lookup.state === "resolved"
      ? PHASE_STEPS.find((step) => step.phase === lookup.sadeSati?.phase)?.phase
      : undefined;

  return (
    <div translate="no" className="space-y-5">
      <p className="text-center text-body-small font-semibold text-text-primary">
        Select your Moon sign to check live
      </p>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
        {rashis.map((rashi) => {
          const isSelected = selected === rashi.id;
          return (
            <button
              key={rashi.id}
              type="button"
              aria-pressed={isSelected}
              onClick={() => void select(rashi.id)}
              className={`flex flex-col items-center gap-0.5 rounded-lg border p-2 text-center transition-colors ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle"
                  : "border-border-default hover:bg-surface-pressed"
              }`}
            >
              <span className="text-body-small font-bold text-text-primary">{rashi.name_en}</span>
              <span className="text-caption text-text-muted">{rashi.name_hi}</span>
            </button>
          );
        })}
      </div>
      <ol className="grid grid-cols-3 gap-2" aria-label="The three Sade Sati phases">
        {PHASE_STEPS.map((step) => {
          const isCurrent = activePhase === step.phase;
          return (
            <li
              key={step.phase}
              aria-current={isCurrent ? "step" : undefined}
              className={`rounded-lg border p-3 text-center ${
                isCurrent ? "border-intent-brand bg-surface-brand-subtle" : "border-border-default"
              }`}
            >
              <span className="block text-body-small font-bold text-text-primary">
                {step.label}
              </span>
              <span className="mt-0.5 block text-caption leading-snug text-text-muted">
                {step.where}
              </span>
            </li>
          );
        })}
      </ol>
      <div aria-live="polite">
        <PhaseStatus lookup={lookup} />
      </div>
    </div>
  );
}

function PhaseStatus({ lookup }: { lookup: Lookup }) {
  if (lookup.state === "idle") return null;
  const rashi = rashis.find((entry) => entry.id === lookup.rashi);
  const signName = rashi ? `${rashi.name_en} (${rashi.name_hi})` : lookup.rashi;
  const frame = "min-h-28 rounded-lg border border-border-default bg-surface-raised p-4";

  if (lookup.state === "loading") {
    return (
      <p className={`${frame} text-body-small text-text-muted`}>
        Checking the live Saturn transit…
      </p>
    );
  }
  if (lookup.state === "error") {
    return (
      <p className={`${frame} text-body-small text-text-secondary`}>
        The live Saturn transit could not be loaded. Tap {signName} again to retry.
      </p>
    );
  }

  const { sadeSati, saturnSign } = lookup;
  return (
    <div className={frame}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-body font-bold text-text-primary">{signName}</span>
        <span
          className={`rounded px-2.5 py-1 text-caption font-semibold uppercase tracking-wider text-text-primary ${
            sadeSati ? "bg-warning/10" : "bg-success/10"
          }`}
        >
          {sadeSati ? `${sadeSati.phase} phase` : "Not active"}
        </span>
      </div>
      <p className="text-body-small leading-relaxed text-text-secondary">
        {sadeSati
          ? sadeSati.phase_description_en || `Sade Sati is running for ${signName}.`
          : `Shani in ${saturnSign ?? "its current sign"} is not in the 12th, 1st, or 2nd sign from this Moon sign.`}
      </p>
      {sadeSati && saturnSign ? (
        <p className="mt-1 text-caption text-text-muted">Shani is in {saturnSign} today.</p>
      ) : null}
    </div>
  );
}
